import React, { useEffect, useRef, useState } from 'react';

// Unique IDs to avoid conflicts with toll-history patterns
export const HexagonPatternDash = ({ className = '', opacity = 0.06 }) => (
  <svg
    className={`absolute pointer-events-none ${className}`}
    width="320"
    height="320"
    viewBox="0 0 320 320"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <defs>
      <pattern id="dash-hex-grid" x="0" y="0" width="40" height="46" patternUnits="userSpaceOnUse">
        <polygon
          points="20,2 38,12 38,34 20,44 2,34 2,12"
          fill="none"
          stroke="#004F98"
          strokeWidth="0.8"
          opacity={opacity}
        />
      </pattern>
    </defs>
    <rect width="320" height="320" fill="url(#dash-hex-grid)" />
  </svg>
);

export const DiagonalStripesDash = ({ className = '', color = '#FF5722', opacity = 0.08 }) => (
  <svg
    className={`absolute pointer-events-none ${className}`}
    width="200"
    height="200"
    viewBox="0 0 200 200"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <defs>
      <pattern id="dash-diag-stripes" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
        <line x1="0" y1="0" x2="0" y2="20" stroke={color} strokeWidth="1.5" opacity={opacity} />
      </pattern>
    </defs>
    <rect width="200" height="200" fill="url(#dash-diag-stripes)" />
  </svg>
);

export const HandDrawnConnectorDash = ({ className = '' }) => (
  <svg
    className={`absolute pointer-events-none ${className}`}
    width="4"
    height="80"
    viewBox="0 0 4 80"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M2 0 C2.5 8, 1.5 16, 2 24 C2.5 32, 1.5 40, 2 48 C2.5 56, 1.5 64, 2 72 C2.3 76, 1.8 78, 2 80"
      stroke="#004F98"
      strokeWidth="1.5"
      strokeLinecap="round"
      opacity="0.4"
    />
    <circle cx="2" cy="0" r="2" fill="#004F98" opacity="0.5" />
    <circle cx="2" cy="80" r="2" fill="#FF5722" opacity="0.6" />
  </svg>
);

export const CrosshairMarkDash = ({ className = '', size = 20, color = '#FF5722' }) => (
  <svg
    className={`absolute pointer-events-none ${className}`}
    width={size}
    height={size}
    viewBox={`0 0 ${size} ${size}`}
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <line x1={size / 2} y1="0" x2={size / 2} y2={size} stroke={color} strokeWidth="1" opacity="0.5" />
    <line x1="0" y1={size / 2} x2={size} y2={size / 2} stroke={color} strokeWidth="1" opacity="0.5" />
    <circle cx={size / 2} cy={size / 2} r="2.5" fill="none" stroke={color} strokeWidth="1" opacity="0.6" />
  </svg>
);

export const TriangleAccentDash = ({ className = '', size = 40, color = '#004F98' }) => (
  <svg
    className={`absolute pointer-events-none ${className}`}
    width={size}
    height={size}
    viewBox={`0 0 ${size} ${size}`}
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <polygon
      points={`${size / 2},2 ${size - 2},${size - 2} 2,${size - 2}`}
      fill="none"
      stroke={color}
      strokeWidth="1.2"
      opacity="0.25"
    />
  </svg>
);

export const HandDrawnUnderlineDash = ({ className = '', width = 120 }) => (
  <svg
    className={`${className}`}
    width={width}
    height="8"
    viewBox={`0 0 ${width} 8`}
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d={`M2 5 C${width * 0.15} 3, ${width * 0.3} 7, ${width * 0.5} 4 C${width * 0.7} 2, ${width * 0.85} 6, ${width - 2} 4`}
      stroke="#FF5722"
      strokeWidth="2"
      strokeLinecap="round"
      fill="none"
      opacity="0.7"
    />
  </svg>
);

export const GridDotsDash = ({ className = '', rows = 5, cols = 8, color = '#004F98', opacity = 0.15 }) => (
  <svg
    className={`absolute pointer-events-none ${className}`}
    width={cols * 16}
    height={rows * 16}
    viewBox={`0 0 ${cols * 16} ${rows * 16}`}
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    {Array.from({ length: rows })?.map((_, r) =>
      Array.from({ length: cols })?.map((_, c) => (
        <circle
          key={`${r}-${c}`}
          cx={c * 16 + 8}
          cy={r * 16 + 8}
          r="1.5"
          fill={color}
          opacity={opacity}
        />
      ))
    )}
  </svg>
);

export const AngularAccentDash = ({ className = '', width = 60, height = 60, color = '#004F98' }) => (
  <svg
    className={`absolute pointer-events-none ${className}`}
    width={width}
    height={height}
    viewBox={`0 0 ${width} ${height}`}
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <polyline
      points={`0,${height} ${width * 0.3},${height} ${width * 0.3},${height * 0.3} ${width},${height * 0.3}`}
      stroke={color}
      strokeWidth="1.5"
      fill="none"
      opacity="0.35"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx={width * 0.3} cy={height * 0.3} r="3" fill={color} opacity="0.4" />
  </svg>
);

// Scroll-triggered reveal hook
export const useScrollReveal = (options = {}) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref?.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: options.threshold || 0.12, rootMargin: options.rootMargin || '0px 0px -40px 0px' }
    );

    observer?.observe(el);
    return () => observer?.disconnect();
  }, []);

  return [ref, isVisible];
};

export default HexagonPatternDash;
